
import { LoadingIcon } from 'morpheus-ui';
import { useEffect, useState } from 'react';
import { ExerciseStatus, ExerciseType } from '../../../models/exercise';
import { Button } from '../../atoms/Button';
import { Icon } from '../../atoms/Icon';
import Radio from '../../atoms/Radio';
import { Typography } from '../Typography';

interface ExerciseAlternativeProps {
  alternativeId: string;
  text: string;
  hint?: string;
  value: string;
  type?: ExerciseType;
  status?: ExerciseStatus;
  selected?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  IsStrikeout?: boolean;
  onSelected: () => void;
  onStrikeout?: (alternativeId: string) => void;
}

const ExerciseAlternative = ({
  alternativeId,
  text,
  hint,
  value,
  status = 'default',
  selected = false,
  disabled = false,
  isLoading = false,
  IsStrikeout,
  onSelected,
  onStrikeout,
}: ExerciseAlternativeProps) => {
  const [isStriked, setIsStriked] = useState<boolean>(IsStrikeout || false);

  useEffect(() => {
    if (IsStrikeout) {
      setIsStriked(IsStrikeout);
    }
  }, [IsStrikeout]);

  const colorStyle = {
    default: 'bg-mono-color-white',
    selected:
      'bg-brand-color-primary-lighter border-mono-color-white border-hairline',
    success:
      'bg-feedback-color-success-lightest border-mono-color-white border-hairline',
    error:
      'bg-feedback-color-error-lightest border-mono-color-white border-hairline',
    correct: 'bg-mono-color-light border-mono-color-white border-hairline',
  };

  const boxColorStyle =
    selected && status === 'default'
      ? colorStyle['selected']
      : colorStyle[status];

  const isLoadingSelected =
    isLoading && selected && status !== 'success' && status !== 'error';

  let checkedColorType:
    | 'default'
    | 'success'
    | 'error'
    | 'correct'
    | 'disabled' = 'default';

  if (status === 'success' || status === 'error' || status === 'correct')
    checkedColorType = status;

  return (
    <div
      className={`relative flex ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      } flex-col gap-sm`}
      onClick={() => {
        if (disabled || isLoading) {
          return;
        }

        setIsStriked(false);
        onSelected();
      }}
    >
      {isLoadingSelected && (
        <LoadingIcon
          name='progress_activity'
          className='stroke-current absolute bottom-0 left-0 right-0 top-0 m-auto h-6 animate-spin fill-none text-brand-color-primary-pure'
        />
      )}
      <div
        className={`flex items-center justify-between rounded-md p-4 shadow-level-1 ${boxColorStyle} ${
          isLoadingSelected ? 'opacity-30' : ''
        }`}
      >
        <div className={`flex flex-col gap-sm`}>
          <Radio
            id={alternativeId}
            label={text}
            value={value}
            checked={selected}
            checkedColorType={checkedColorType}
            onChange={() => {}}
            disabled={isLoading || disabled}
            className={`${
              disabled && checkedColorType !== 'correct'
                ? 'text-mono-color-medium'
                : ''
            } ${isStriked ? 'text-mono-color-darkest line-through' : ''}`}
          />
          {hint && (
            <Typography size='small' className='ml-8 text-mono-color-darker'>
              {hint}
            </Typography>
          )}
        </div>
        {status === 'default' && !selected && !disabled && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setIsStriked(!isStriked);

              onStrikeout && onStrikeout(alternativeId);
            }}
            variant='text'
            onlyIcon
          >
            <Icon
              type='material'
              Icon={isStriked ? 'check-circle' : 'cancel'}
              className='text-md'
            />
          </Button>
        )}
        {status === 'success' && (
          <Icon
            Icon='check'
            type='material'
            className='text-base text-feedback-color-success-darkest'
          />
        )}
        {status === 'error' && (
          <Icon
            Icon='close'
            type='material'
            className='text-base text-feedback-color-error-darkest'
          />
        )}
      </div>
    </div>
  );
};

export default ExerciseAlternative;
